/**
 * UploadedFileItem Component
 * 
 * Individual uploaded file indicator for use in chat input area.
 * Shows file type icon and name, with hover state showing remove (X) icon.
 * 
 * Features:
 * - Compact design optimized for input area
 * - File type icon display (reuses existing utilities)
 * - Hover state with X icon for removal
 * - Tooltip showing full filename if truncated
 * - Same styling as message attachment items but compact
 */

import React, { useCallback } from 'react';
import { Box, Text, Tooltip, Loader } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { getFileTypeIconFromFilename } from '../../AttachmentItem/utils';
import type { UploadedFileItemProps } from './types';
import classes from './UploadedFileItem.module.css';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const UploadedFileItem: React.FC<UploadedFileItemProps> = ({
  filename,
  conversationId,
  onRemove,
  compact = true,
  className,
  isUploading,
  isDeleting,
}) => {
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  const handleRemove = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.(filename);
  }, [onRemove, filename]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Handle Enter and Space for accessibility
    if ((event.key === 'Enter' || event.key === ' ') && onRemove) {
      event.preventDefault();
      event.stopPropagation();
      onRemove(filename);
    }
  }, [onRemove, filename]);

  // =============================================================================
  // DERIVED STATE
  // =============================================================================
  
  const FileIconComponent = getFileTypeIconFromFilename(filename);
  const displayFileName = filename;
  
  // Determine if tooltip is needed (for longer filenames)
  const needsTooltip = filename.length > 25;
  const tooltipLabel = isDeleting
    ? `${filename} (deleting…)`
    : isUploading
      ? `${filename} (uploading…)`
      : `${filename} (uploaded, ready to send)`;
  const tooltipDisabled = !isDeleting && !isUploading && !needsTooltip;

  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <Tooltip 
      label={tooltipLabel}
      position="top"
      disabled={tooltipDisabled}
    >
      <Box
        className={`${classes.uploadedFileItem} ${className || ''}`}
        data-deleting={isDeleting ? 'true' : undefined}
        data-uploading={isUploading ? 'true' : undefined}
      >
        {/* Icon Container - shows file icon normally, X icon on hover */}
        <Box className={classes.iconContainer}>
          {/* Uploading/Deleting state shows loader (blue/red) */}
          {isUploading || isDeleting ? (
            <Loader size={14} type="oval" color={isDeleting ? 'red' : undefined} />
          ) : (
            <Box className={classes.fileIcon}>
              <FileIconComponent size={14} />
            </Box>
          )}
          
          {/* Remove Icon - visible on hover when removable */}
          {onRemove && !isUploading && !isDeleting && (
            <Box className={classes.removeIcon}>
              <button
                type="button"
                className={classes.removeButton}
                onClick={handleRemove}
                onKeyDown={handleKeyDown}
                aria-label={`Remove ${filename}`}
              >
                <IconX size={14} />
              </button>
            </Box>
          )}
        </Box>

        {/* File Name */}
        <Text className={classes.fileName} data-uploading={isUploading ? 'true' : undefined}>
          {displayFileName}
        </Text>
      </Box>
    </Tooltip>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default UploadedFileItem;
